/* IotOsPlanGridViewRenderer.js */

"use strict";

const EntityGrid = require("@gluenetworks/entity_grid_gl").Grid;
const Registrations = require("../../../../../RegistrationNamespaces");
const _ = require("lodash");

class IotOsPlanGridViewRenderer {
    constructor(model) {
        this._model = model;
        this._rootElem = document.createElement("iot-os-plan-grid-view-gl");
        this._logger = this._model.logger;
        this._defaultViewActive = false;
    }

    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/

    _registerListeners() {
        this._model.on(
            "FeedInitialized",
            (this._onFeedInitialized = this._onFeedInitialized.bind(this))
        );
        this._model.pagingFeed.on(
            "RefreshView",
            (this._onFeedRefreshView = this._onFeedRefreshView.bind(this))
        );
        this._registerGridListeners();
    }

    _registerGridListeners() {
        this._osPlanEntityGrid.on(
            "UserSelectedRows",
            (this._onGridUserSelectedRows = this._onGridUserSelectedRows.bind(this))
        );
        this._osPlanEntityGrid.on(
            "UserRowDoubleClicked",
            (this._onUserDblClickedRow = this._onUserDblClickedRow.bind(this))
        );
        this._osPlanEntityGrid.on(
            "UserSorted",
            (this._onUserSorted = this._onUserSorted.bind(this))
        );
        this._osPlanEntityGrid.on(
            "GridBusyStart",
            (this._onGridBusyStart = this._onGridBusyStart.bind(this))
        );
    }

    _unregisterListeners() {
        if (this._model) {
            this._model.removeListener("FeedInitialized", this._onFeedInitialized);
            if (this._model.pagingFeed) {
                this._model.pagingFeed.removeListener("RefreshView", this._onFeedRefreshView);
            }
        }
        if (this._osPlanEntityGrid) {
            this._unregisterGridListeners();
        }
    }

    _unregisterGridListeners() {
        this._osPlanEntityGrid.removeAllListeners("UserSelectedRows");
        this._osPlanEntityGrid.removeAllListeners("UserRowDoubleClicked");
        this._osPlanEntityGrid.removeListener("UserSorted", this._onUserSorted);
        this._osPlanEntityGrid.removeListener("GridBusyStart", this._onGridBusyStart);
    }

    async _renderAvailableActions(plans = []) {
        let numSelected = this.selectedRows.length || 0;

        if (numSelected > 0) {
            this._model.gameEditor.enableAllActions();
        } else {
            this._model.gameEditor.disableAllActions();
            this._model.gameEditor.enableAction(Registrations.IOT_OS_PLAN_EXPLORER_ACTIONS_CREATE);
        }

        if (numSelected !== 1) {
            this._model.gameEditor.disableAction(Registrations.IOT_OS_PLAN_EXPLORER_ACTIONS_EDIT);
        }

        const planEntities = await this._model.pagingFeed.getIotOsPlanEntitiesById(plans);
        if (!_.isEmpty(planEntities)) {
            if (
                planEntities.some(
                    (planEntity) => planEntity && planEntity.getStatus() !== "VALIDATED"
                ).length > 0
            ) {
                this._model.gameEditor.disableAction(
                    Registrations.IOT_OS_PLAN_EXPLORER_ACTIONS_RUN
                );
            } else {
                this._model.gameEditor.enableAction(Registrations.IOT_OS_PLAN_EXPLORER_ACTIONS_RUN);
            }
        }

        if (numSelected > 1) {
            this._model.gameEditor.disableAction(Registrations.IOT_OS_PLAN_EXPLORER_ACTIONS_CLONE);
        }
    }

    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/

    shutdown() {
        this._unregisterListeners();
        let promArr = [_.invoke(this._osPlanEntityGrid, "shutdown") || Promise.resolve()];

        return Promise.all(promArr).then(
            () => {
                this._osPlanEntityGrid = undefined;
                this._defaultViewActive = undefined;
                this._model = undefined;
                this._rootElem.remove();
                this._rootElem = undefined;
            },
            (err) => {
                this.model._logger.warn(err, "OsPlanGridViewRenderer.shutdown() error");
            }
        );
    }

    /**
     * Stateless method to completely rebuild this component from the _rootElem down.
     * @returns {Promise} An ES6 promise that lets the caller know when the component has finished rendering. If a non
     * Promise is returned the caller will assume the Renderer has completed rendering.
     */
    render() {
        return this._model
            .whenModelIsReady()
            .then(() => {
                this._updatePagingFeedUserColumns();
                this._osPlanEntityGrid = new EntityGrid({
                    gridKey: this.model._gridKey,
                    logger: this._logger,
                    singleSelect: false,
                    selectedRows: _.get(this._model.getSelectedPlanIds(), "selectedRows", []),
                    columns: this._model.columnConfig,
                    pagingFeed: this._model.pagingFeed,
                    showHeader: true,
                    showLinesNumber: this._model._gameEditor.renderer._rootElem.querySelector(
                        "game-editor-view-actions-gl left-container"
                    ),
                    columnSettingsWidgetContainer:
                        this._model._gameEditor.renderer._rootElem.querySelector(".ge-header-left"),
                    filterComponent:
                        this._model._gameEditor.renderer._rootElem.querySelector(".ge-header-left"),
                    advancedFilterComponent:
                        this._model._gameEditor.renderer._rootElem.querySelector(".ge-header-left"),
                    modalUserFeedback: this._model._modalUserFeedback,
                    userGridData: this._model._userGridData,
                });

                if (!this._rootElem.hasChildNodes()) {
                    this._rootElem.appendChild(this._osPlanEntityGrid.renderer.rootElement);
                }
                this._renderAvailableActions();
                this._onFeedRefreshView();
                this._registerListeners();
            })
            .then(() => {
                if (this._model._isItFromDeviceExplorer) {
                    this._openInstanceEditor(this._model.explorerState.selectedIds.selectedRows);
                } else if (
                    this._model._gameEditor._openEditorFromUrl &&
                    (this._model._gameEditor._openEditorFromUrl.open ||
                        !_.isUndefined(this._model._gameEditor._openEditorFromUrl.selectedIds))
                ) {
                    this._openInstanceEditor(
                        this._model._gameEditor._openEditorFromUrl.selectedIds
                    );
                }

                if (this._model._gameEditor._filterFromUrl) {
                    this._osPlanEntityGrid._advancedFilter._advancedFilter._renderer._applyQuery();
                }
            });
    }

    _openInstanceEditor(selectedRows) {
        this._model.openPlanInstanceEditor(selectedRows);
        this._model.resetSelection(selectedRows);
        this._model.loadAndScrollTo(selectedRows[0]);
    }

    get rootElement() {
        return this._rootElem;
    }

    get model() {
        return this._model;
    }

    get selectedRows() {
        return this._osPlanEntityGrid.getSelectedRows();
    }

    restartGrid() {
        //this._unregisterGridListeners();
        return this._osPlanEntityGrid.shutdown().then(() => {
            this._instantiateGrid();
            this._rootElem.appendChild(this._osPlanEntityGrid.renderer.rootElement);
            this._registerGridListeners();
            this._renderAvailableActions();
        });
    }

    /*******************************************/
    /* END COMPONENT RENDERER METHODS          */
    /* START PRIVATE RENDER METHODS            */
    /*******************************************/

    _updatePagingFeedUserColumns() {
        this._model.pagingFeed._userColumns.forEach((col) => {
            Object.assign(col, this._model.columnConfig[col.name]);
        });
    }

    _showDefaultMessageDisplay() {
        let message;
        switch (this._model._solutionName) {
            case "os_manager":
                message = {
                    message: "Create a new IoT OS plan",
                    icon: "osmanager.add_os_plan",
                    action: Registrations.IOT_OS_PLAN_EXPLORER_ACTIONS_CREATE,
                };
                break;
        }
        return this._model.gameEditor
            .showDefaultMessageView(message)
            .then(() => {
                this._defaultViewActive = true;
            })
            .catch((err) => {
                this._logger.warn(
                    {error: err},
                    "IotOsPlanGridViewRenderer._showDefaultMessageDisplay error"
                );
            });
    }

    _hideDefaultMessageDisplay() {
        return this._model.gameEditor
            .hideDefaultMessageView()
            .then(() => {
                this._defaultViewActive = false;
            })
            .catch((err) => {
                this._logger.warn(
                    {error: err},
                    "OsPlanGridViewRenderer._hideDefaultMessageDisplay error"
                );
            });
    }

    /*******************************************/
    /* END PRIVATE RENDER METHODS              */
    /* START HANDLER METHODS                   */
    /*******************************************/

    _onGridBusyStart() {
        if (this._model.pagingFeed.totalEntities > this._model.pagingFeed.pageSize * 2) {
            let gridBusyEnded = false;
            this._osPlanEntityGrid.once("GridBusyEnd", () => {
                gridBusyEnded = true;
            });
            this._model._modalUserFeedback.displayLoading().then(() => {
                if (gridBusyEnded) {
                    this._model._modalUserFeedback.hideDisplayed();
                } else {
                    this._osPlanEntityGrid.once("GridBusyEnd", () => {
                        this._model._modalUserFeedback.hideDisplayed();
                    });
                }
            });
        }
    }

    _onFeedInitialized() {
        this._renderAvailableActions();
    }

    _onUserSorted(eventData) {
        this._model.onUserSorted(eventData);
    }

    _onUserDblClickedRow(obj) {
        let plans = this._model.pagingFeed.getIotOsPlanEntitiesById([obj.selectedRow]);
        let status = ["UPGRADE_COMPLETE", "UPGRADE_INCOMPLETE", "UPGRADE_ERROR"];
        if (status.indexOf(plans.status) >= 0) {
            //@toDo: rewrite to Plan.status
            this._model.openPlanInstanceEditor(
                obj.selectedRow,
                Registrations.IOT_OS_PLAN_EDITOR_VIEW_RESULTS
            );
        } else {
            // Default DIE view if neither in Config Drift nor in Policy Manager
            this._model.openPlanInstanceEditor(
                obj.selectedRow,
                Registrations.IOT_OS_PLAN_EDITOR_VIEW_DETAILS
            );
        }
    }

    _onGridUserSelectedRows(selectedRowIds) {
        this._model.updateExplorerState("selectedIds", selectedRowIds || []);
        this._renderAvailableActions(selectedRowIds.selectedRows);
    }

    _onFeedRefreshView() {
        const totalEntities = this._model.pagingFeed.totalEntities;
        const filterData = this._model.pagingFeed.getFilterData();

        if (totalEntities === 0 && _.isEmpty(filterData)) {
            if (this._defaultViewActive === false) {
                this._showDefaultMessageDisplay();
            }
        } else {
            if (this._defaultViewActive === true) {
                this._hideDefaultMessageDisplay();
            }
        }
    }

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE API METHODS               */
    /*******************************************/

    // TODO - Nobody should be using methods on a Renderer directly so make all these methods private

    /*******************************************/
    /*  END PRIVATE API METHODS */
    /*******************************************/
}

module.exports = IotOsPlanGridViewRenderer;
